import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PageSize } from 'src/app/enumeration/pageSize';
import { LmsApiService } from 'src/app/service/lms/lms-api-service';

@Component({
  selector: 'app-lms-course-item',
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './lms-course-item.html',
  styleUrl: './lms-course-item.css',
})
export class LmsCourseItem implements OnInit {

  private readonly router = inject(Router);
  private readonly apiService = inject(LmsApiService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly courseItemList = signal<any[]>([]);
  readonly courseCatalogNameList = signal<any[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1);
  readonly pageSize = signal<number>(PageSize.DEFAULT_LIST);

  readonly apiLoading = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  courseForm: FormGroup;
  updateForm: FormGroup<any>;

  constructor() {
    this.courseForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      points: ['', [Validators.required, Validators.min(0)]],
      catalogName: ['', [Validators.required]],
      redirectUrl: [''],
      description: ['', [Validators.maxLength(512)]]
    });

    this.updateForm = this.fb.group({
      courseItemId: ['', Validators.required],
      name: ['', [Validators.required, Validators.minLength(3)]],
      points: [{ value: '', disabled: true }, Validators.required],
      redirectUrl: [''],
      description: ['', [Validators.maxLength(512)]]
    });
  }

  ngOnInit(): void {
    this.fetchCourseItemRecords();
    this.fetchCourseCatalogNameList();
  }

  fetchCourseCatalogNameList() {

    this.apiService.getLMSCourseCatalogNameList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any) => {
          this.courseCatalogNameList.set(v?.Message || []);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching catalog records.');
        }
      });
  }

  fetchCourseItemRecords() {
    this.loading.set(true);

    const zeroBasedPage = this.page() - 1;

    this.apiService.getLMSCourseItemList(zeroBasedPage)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.courseItemList.set(v?.Message.lmsCourseItemInfoRespLst || []);
          this.total.set(v?.Message.pageTotal || 0);
          this.allTotal.set(v?.Message.allTotal || 0);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching course item records.');
        }
      });
  }

  onSubmitCourse() {
    if (this.courseForm.invalid) return;

    this.apiLoading.set(true);
    const formData = this.courseForm.value;

    this.apiService.getCreateLMSCourseItem(formData.name, formData.points, formData.catalogName, formData.redirectUrl, formData.description)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.showSuccess('Course item created successfully.');
          this.courseForm.reset();
          this.fetchCourseItemRecords();
          this.closeModalViaNativeElement('addNewCourse')
        },
        error: (err: any) => {
          this.closeModalViaNativeElement('addNewCourse')
          this.showError(err || 'Network error occurred while creating course item.');
        }
      });
  }

  toggleCatalogStatus(course: any) {
    this.apiLoading.set(true);

    this.apiService.getEnabledDisabledLMSCourseItem(course.name, !course.enabled)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          this.showSuccess('Successfully updated course item status.');
          this.fetchCourseItemRecords();
        },
        error: (error: any) => {
          this.showError(error || 'A network error occurred while updating the course item status.');
        }
      });
  }

  onOpenUpdateDrawer(course: any): void {
    this.updateForm.patchValue({
      courseItemId: course.id,
      name: course.name,
      points: course.points,
      redirectUrl: course.redirectUrl || '',
      description: course.description || ''
    });
  }

  onRemoveCourse(redeemId: any) {
    
    this.apiLoading.set(true);
    this.apiService.getRemoveLMSCourseItem(redeemId)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.showSuccess('Course item removed successfully.');
          this.fetchCourseItemRecords();
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while removing course item.');
        }
      });
  }

  submitUpdateQuery() {
    if (this.updateForm.invalid) return;

    this.apiLoading.set(true);
    const formValues = this.updateForm.value;

    this.apiService.getUpdateLMSCourseItem(formValues.courseItemId, formValues.name, formValues.redirectUrl, formValues.description)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.showSuccess('Course item updated successfully.');
          this.updateForm.reset();
          this.fetchCourseItemRecords();
          this.closeModalViaNativeElement('updateCourseDrawer')
        },
        error: (err: any) => {
          this.closeModalViaNativeElement('updateCourseDrawer')
          this.showError(err || 'Network error occurred while updating course item.');
        }
      });

  }

  goToCatalogList() {
    this.router.navigate(['nav/lms-course-catalog/']);
  }

  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.fetchCourseItemRecords();
    }
  }

  showError(msg: string): void {
    this.errorMessage.set(msg);
    // Auto clear error banner after 6 seconds
    setTimeout(() => this.clearAlerts(), 6000);
  }

  showSuccess(msg: string): void {
    this.successMessage.set(msg);
    // Auto clear success banner after 4 seconds
    setTimeout(() => this.clearAlerts(), 4000);
  }

  clearAlerts(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }
}
